import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  isDefault: boolean; // Indicates if this column is part of the default view
  category?: 'basic' | 'details' | 'dates' | 'status';
}

interface ColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => void;
  onReset: () => void;
}

export function ColumnConfigDialog({
  open,
  onOpenChange,
  columns,
  onSave,
  onReset,
}: ColumnConfigDialogProps) {
  const [localColumns, setLocalColumns] = useState<ColumnConfig[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalColumns([...columns].sort((a, b) => a.order - b.order));
      setHasChanges(false);
    }
  }, [open, columns]);

  const handleToggleVisibility = (columnId: string) => {
    const updated = localColumns.map((col) =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setLocalColumns(updated);
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...localColumns];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;
    // Update order values
    updated.forEach((col, idx) => {
      col.order = idx;
    });
    setLocalColumns(updated);
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === localColumns.length - 1) return;
    const updated = [...localColumns];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;
    // Update order values
    updated.forEach((col, idx) => {
      col.order = idx;
    });
    setLocalColumns(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(localColumns);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleReset = () => {
    onReset();
    onOpenChange(false);
  };

  const getCategoryLabel = (category?: string) => {
    switch (category) {
      case 'basic':
        return 'Basic Information';
      case 'details':
        return 'Case Details';
      case 'dates':
        return 'Date Information';
      case 'status':
        return 'Status & Priority';
      default:
        return 'Other';
    }
  };

  const groupedColumns = localColumns.reduce((acc, col) => {
    const category = col.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(col);
    return acc;
  }, {} as Record<string, ColumnConfig[]>);

  const visibleCount = localColumns.filter((col) => col.visible).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogDescription>
            Select which columns to display and arrange their order. Changes will be saved to your profile.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {visibleCount} of {localColumns.length} columns visible
            </Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </Button>
        </div>

        <Separator />

        <div className="h-[400px] overflow-y-auto border rounded-md p-4">
          <div className="space-y-6">
            {Object.entries(groupedColumns).map(([category, cols]) => (
              <div key={category} className="space-y-2">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {getCategoryLabel(category)}
                </h4>
                <div className="space-y-1">
                  {cols.map((column) => {
                    const globalIndex = localColumns.findIndex((c) => c.id === column.id);
                    return (
                      <div
                        key={column.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors",
                          !column.visible && "opacity-60"
                        )}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                          <Checkbox
                            id={`column-${column.id}`}
                            checked={column.visible}
                            onCheckedChange={() => handleToggleVisibility(column.id)}
                          />
                          <Label
                            htmlFor={`column-${column.id}`}
                            className="flex-1 cursor-pointer font-medium"
                          >
                            {column.label}
                            {column.isDefault && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                Default
                              </Badge>
                            )}
                          </Label>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveUp(globalIndex)}
                            disabled={globalIndex === 0}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveDown(globalIndex)}
                            disabled={globalIndex === localColumns.length - 1}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Separator />

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges}>
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
